import React from 'react';

import type { Question } from 'types';
import { normalizeQuestionType } from 'utils/questionTypeUtils';
import { Card } from '../ui/card';

interface QuestionLoadingSkeletonProps {
  question?: Pick<Question, 'question_type' | 'question_type_display'>;
  loadingLabel?: string;
}

export const QuestionLoadingSkeleton: React.FC<QuestionLoadingSkeletonProps> = ({
  question,
  loadingLabel = 'Loading question',
}) => {
  const questionType = normalizeQuestionType(question?.question_type_display ?? question?.question_type);
  const hasOptionRows = ['multiple_choice', 'multiple_select', 'drag_and_drop_ranking', 'text_list'].includes(questionType);

  return (
    <Card
      maxWidth="none"
      padding="none"
      variant="glass"
      disableAnimation
      className="w-full max-w-[calc(100vw-3.25rem)] min-w-0 pt-5 motion-safe:animate-pulse sm:max-w-[41.25rem]"
      role="status"
      aria-live="polite"
      data-flow-surface="card"
      data-testid="question-loading-skeleton"
    >
      <span className="sr-only">{loadingLabel}</span>
      <div aria-hidden="true">
        <div className="space-y-5 px-5 pb-6 sm:px-10">
          <div className="h-3 w-2/5 rounded bg-black/10" />
          <div className="space-y-3">
            <div className="h-7 w-full rounded bg-black/10" />
            <div className="h-7 w-4/5 rounded bg-black/10" />
          </div>
          <div className="h-4 w-3/5 rounded bg-black/10" />
        </div>
        <div className="bg-white/45 px-5 py-8 sm:px-8">
          {questionType === 'rating' ? (
            <div className="space-y-6">
              <div className="flex h-28 items-end justify-between gap-2">
                {[100, 70, 40, 10, 40, 70, 100].map((height, index) => (
                  <div key={index} className="w-2 rounded bg-black/10" style={{ height: `${height}%` }} />
                ))}
              </div>
              <div className="h-2 w-full rounded-full bg-black/10" />
              <div className="flex justify-between gap-6">
                <div className="h-4 w-1/3 rounded bg-black/10" />
                <div className="h-4 w-1/3 rounded bg-black/10" />
              </div>
            </div>
          ) : questionType === 'boolean' ? (
            <div className="flex gap-4 py-4">
              <div className="h-14 flex-1 rounded-xl bg-black/10" />
              <div className="h-14 flex-1 rounded-xl bg-black/10" />
            </div>
          ) : hasOptionRows ? (
            <div className="space-y-3">
              {[0, 1, 2].map((row) => <div key={row} className="h-12 rounded-xl bg-black/10" />)}
            </div>
          ) : (
            <div className="h-36 rounded-xl bg-black/10" />
          )}
        </div>
        <div className="px-5 pb-7 pt-4 sm:px-10">
          <div className="h-12 w-32 rounded-full bg-black/10" />
        </div>
      </div>
    </Card>
  );
};
